import { type ReactNode, useState } from 'react'
import cx from 'classnames'
import { Link } from 'react-router'
import { capitalize } from 'lodash-es'
import { format } from 'date-fns'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { FlatCardsContext } from '@/components/Card'
import { IconButton } from '@/components/IconButton'
import { Button } from '@/components/Button'
import { Tooltip } from '@/components/Tooltip'
import {
  Dropdown,
  DropdownItem,
  DropdownItemTitle,
  DropdownTitle,
} from '@/components/Dropdown'
import { formatMoney } from '@/utils/money-util'
import { type PrepAndReviewLinkType } from '@/features/jobs/job-type'
import styles from './JobDetailsRail.module.css'

export interface JobDetailsRailProps {
  children: ReactNode
  moreCards: ReactNode
}

/**
 * The job's reference facts beside the main column: its results, properties,
 * the people on it and what it covers.
 */
export function JobDetailsRail({ children, moreCards }: JobDetailsRailProps) {
  return (
    <aside
      className={styles.root}
      aria-label="Job properties"
      data-test="job-details-rail"
    >
      {children}
      <FlatCardsContext.Provider value>{moreCards}</FlatCardsContext.Provider>
    </aside>
  )
}

export interface JobDetailsRailGroupProps {
  title: string
  action?: ReactNode
  children: ReactNode
}

export function JobDetailsRailGroup({
  title,
  action,
  children,
}: JobDetailsRailGroupProps) {
  return (
    <section className={styles.group}>
      <div className={styles.groupHead}>
        <span className={styles.groupTitle}>{title}</span>
        {action}
      </div>
      {children}
    </section>
  )
}

interface RowProps {
  label: string
  children: ReactNode
  testId?: string
}

function Row({ label, children, testId }: RowProps) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value} data-test={testId}>
        {children}
      </span>
    </div>
  )
}

export function JobDetailsRailReturnResultsPlaceholder() {
  return (
    <JobDetailsRailGroup title="Return results">
      <span className={styles.hint}>
        Appears for each tax year once its final return is uploaded and
        extracted.
      </span>
    </JobDetailsRailGroup>
  )
}

export interface JobDetailsRailReturnResultsProps {
  net: number
  yearCount: number
  manualCount: number
  onViewBreakdown: () => unknown
  children?: ReactNode
}

export function JobDetailsRailReturnResults({
  net,
  yearCount,
  manualCount,
  onViewBreakdown,
  children,
}: JobDetailsRailReturnResultsProps) {
  return (
    <JobDetailsRailGroup
      title="Return results"
      action={
        <Button
          label="View breakdown"
          variant="text"
          size="baseline"
          onPress={onViewBreakdown}
        />
      }
    >
      <Row label={net < 0 ? 'Net owed' : 'Net refund'}>
        <b>{formatMoney(Math.abs(net))}</b>
      </Row>
      <Row label="Returns">
        {yearCount} {yearCount === 1 ? 'year' : 'years'}
        {' · '}
        {yearCount - manualCount} auto-extracted, {manualCount} manual
      </Row>
      {children}
    </JobDetailsRailGroup>
  )
}

export interface JobDetailsRailPrepAndReview {
  text: string
  linkType: PrepAndReviewLinkType | null | undefined
  onView: () => unknown
}

export interface JobDetailsRailPropertiesProps {
  menu?: ReactNode
  shortId: string
  workspaceName: string
  showLegend: boolean
  legendText?: string
  bundleName?: string
  serviceItemCount: number
  providerCutAmount?: string
  showTotal: boolean
  total: number
  payoutSplit?: 'Allocated' | 'Automated'
  dateAccepted?: Date | null
  dateSubmitted?: Date | null
  dateDeadline?: Date | null
  prepAndReview?: JobDetailsRailPrepAndReview
  canSplitPayout: boolean
  onCopyId: () => unknown
  onEditSplit: () => unknown
}

export function JobDetailsRailProperties({
  menu,
  shortId,
  workspaceName,
  showLegend,
  legendText,
  bundleName,
  serviceItemCount,
  providerCutAmount,
  showTotal,
  total,
  payoutSplit,
  dateAccepted,
  dateSubmitted,
  dateDeadline,
  prepAndReview,
  canSplitPayout,
  onCopyId,
  onEditSplit,
}: JobDetailsRailPropertiesProps) {
  return (
    <JobDetailsRailGroup title="Properties" action={menu}>
      <Row label="ID" testId="job-short-id">
        <span className={styles.mono}>{shortId}</span>
        <Button
          label="Copy"
          variant="text"
          size="baseline"
          onPress={onCopyId}
        />
      </Row>
      <Row label="Workspace">{workspaceName}</Row>
      {showLegend && (
        <Row label="Legend" testId="job-legend-name">
          {legendText}
        </Row>
      )}
      {bundleName !== undefined && <Row label="Bundle">{bundleName}</Row>}
      {serviceItemCount > 0 && (
        <Row label="Service items">
          {serviceItemCount} {serviceItemCount === 1 ? 'item' : 'items'}
        </Row>
      )}
      {providerCutAmount !== undefined && (
        <Row label="Pro cut">
          <b>{providerCutAmount}</b>
        </Row>
      )}
      {showTotal && (
        <Row label="Total" testId="job-total">
          {formatMoney(total)}
        </Row>
      )}
      {payoutSplit && <Row label="Payout split">{payoutSplit}</Row>}
      {dateAccepted && <Row label="Started">{shortDate(dateAccepted)}</Row>}
      {dateSubmitted && <Row label="Submitted">{shortDate(dateSubmitted)}</Row>}
      {dateDeadline && <Row label="Deadline">{shortDate(dateDeadline)}</Row>}
      {prepAndReview && (
        <Row
          label="Prep & review"
          testId={`prep-and-review-job-type-${prepAndReview.text.toUpperCase()}`}
        >
          {`${capitalize(prepAndReview.text)} job`}
          <Button
            label="View"
            variant="text"
            size="baseline"
            onPress={prepAndReview.onView}
            testId={`prep-and-review-job-type-${prepAndReview.linkType?.toUpperCase()}-btn`}
          />
        </Row>
      )}
      {canSplitPayout && (
        <div className={styles.after}>
          <Button
            label="Edit split"
            variant="text"
            size="baseline"
            onPress={onEditSplit}
          />
        </div>
      )}
    </JobDetailsRailGroup>
  )
}

export interface JobDetailsRailPropertiesMenuProps {
  canRename: boolean
  canUpdateDeadline: boolean
  canUpdateJobScope: boolean
  onRename: () => unknown
  onUpdateDeadline: () => unknown
  onUpdateJobScope: () => unknown
}

export function JobDetailsRailPropertiesMenu({
  canRename,
  canUpdateDeadline,
  canUpdateJobScope,
  onRename,
  onUpdateDeadline,
  onUpdateJobScope,
}: JobDetailsRailPropertiesMenuProps) {
  if (!canUpdateDeadline && !canUpdateJobScope && !canRename) {
    return null
  }
  return (
    <Dropdown
      autoDismiss
      position="bottom-end"
      button={<IconButton name="more" label="Job property actions" />}
    >
      <DropdownTitle>Actions</DropdownTitle>
      {canRename && (
        <DropdownItem onPress={onRename}>
          <DropdownItemTitle>Rename job</DropdownItemTitle>
        </DropdownItem>
      )}
      {canUpdateDeadline && (
        <DropdownItem onPress={onUpdateDeadline}>
          <DropdownItemTitle>Update deadline</DropdownItemTitle>
        </DropdownItem>
      )}
      {canUpdateJobScope && (
        <DropdownItem onPress={onUpdateJobScope}>
          <DropdownItemTitle>Update job scope</DropdownItemTitle>
        </DropdownItem>
      )}
    </Dropdown>
  )
}

export interface JobDetailsRailInfoLinesProps {
  lines: { title: string; content: string }[]
}

export function JobDetailsRailInfoLines({
  lines,
}: JobDetailsRailInfoLinesProps) {
  const [expanded, setExpanded] = useState(false)

  return (
    <JobDetailsRailGroup title={`Info lines · ${lines.length}`}>
      {lines.map((line, index) => (
        <div key={index} className={styles.infoLine}>
          <span className={styles.infoTitle}>{line.title}</span>
          <span className={cx(styles.infoBody, !expanded && styles.clamped)}>
            {line.content}
          </span>
        </div>
      ))}
      <Button
        label={expanded ? 'Show less' : 'Show more'}
        variant="text"
        size="baseline"
        onPress={() => setExpanded(!expanded)}
      />
    </JobDetailsRailGroup>
  )
}

export interface JobDetailsRailPeopleProps {
  menu: ReactNode
  children: ReactNode
}

export function JobDetailsRailPeople({
  menu,
  children,
}: JobDetailsRailPeopleProps) {
  return (
    <JobDetailsRailGroup title="People" action={menu}>
      <div className={styles.people}>{children}</div>
    </JobDetailsRailGroup>
  )
}

export interface JobDetailsRailPersonProps {
  avatar: AvatarSubject
  online: boolean
  displayName: string
  formattedType: string
  isCurrentUser: boolean
  userDetailsLink?: string
  showCollaboratorInfo: boolean
  teamName?: string
  email?: string
  phone?: string
  formattedRole: string
}

export function JobDetailsRailPerson({
  avatar,
  online,
  displayName,
  formattedType,
  isCurrentUser,
  userDetailsLink,
  showCollaboratorInfo,
  teamName,
  email,
  phone,
  formattedRole,
}: JobDetailsRailPersonProps) {
  const name = (
    <span className={styles.personName}>
      {displayName}
      <span className={styles.personRole}>
        {' · '}
        {formattedType}
        {isCurrentUser ? ' (you)' : ''}
      </span>
    </span>
  )

  return (
    <div
      className={styles.person}
      data-test={`collaborator-item-${displayName}`}
    >
      <Avatar
        size={26}
        subject={avatar}
        badge={online ? 'online' : 'offline'}
      />
      <div className={styles.personText}>
        {userDetailsLink !== undefined ? (
          <Link to={userDetailsLink}>{name}</Link>
        ) : (
          name
        )}
        {showCollaboratorInfo && (
          <>
            {teamName !== undefined && (
              <span className={styles.personMeta}>{teamName}</span>
            )}
            {email && <span className={styles.personMeta}>{email}</span>}
            {phone && (
              <span className={cx(styles.personMeta, styles.mono)}>
                {phone}
              </span>
            )}
          </>
        )}
        <span className={styles.personMeta}>{formattedRole}</span>
      </div>
    </div>
  )
}

export interface JobDetailsRailServicesProps {
  items: { name: string; quantity: number }[]
  onViewAll: () => unknown
}

export function JobDetailsRailServices({
  items,
  onViewAll,
}: JobDetailsRailServicesProps) {
  if (items.length === 0) {
    return null
  }

  return (
    <JobDetailsRailGroup
      title={`Services · ${items.length}`}
      action={
        <Button
          label="View all"
          variant="text"
          size="baseline"
          onPress={onViewAll}
          testId="service-items-view-all"
        />
      }
    >
      <div className={styles.chips}>
        {items.map((item, index) => (
          <span key={index} className={styles.chip}>
            {item.name}
            {item.quantity > 1 && (
              <span className={styles.chipCount}>×{item.quantity}</span>
            )}
          </span>
        ))}
      </div>
    </JobDetailsRailGroup>
  )
}

export interface JobDetailsRailAmendment {
  description: string
  customerAmount: number
  internalNote: string
  visibility: string
  providerAmount: number
  dateCreated: Date
}

export interface JobDetailsRailAmendmentsProps {
  amendments: JobDetailsRailAmendment[]
  canAdd: boolean
  onAdd: () => unknown
}

export function JobDetailsRailAmendments({
  amendments,
  canAdd,
  onAdd,
}: JobDetailsRailAmendmentsProps) {
  if (amendments.length === 0) {
    return (
      <JobDetailsRailGroup title="Amendments">
        <span className={styles.hint}>
          Refunds, added or reduced services, and extra Pro earnings are
          recorded here.
        </span>
        {canAdd && (
          <div className={styles.after}>
            <Button
              label="Add amendment"
              variant="text"
              size="baseline"
              onPress={onAdd}
              testId="add-amendment-button"
            />
          </div>
        )}
      </JobDetailsRailGroup>
    )
  }

  return (
    <JobDetailsRailGroup
      title={`Amendments · ${amendments.length}`}
      action={
        canAdd && (
          <Tooltip content="Add an amendment">
            <IconButton
              name="add"
              label="Add amendment"
              onPress={onAdd}
              testId="add-amendment-button"
            />
          </Tooltip>
        )
      }
    >
      <ul className={styles.amendments}>
        {amendments.map((amendment, index) => (
          <li key={index} className={styles.amendment}>
            <div className={styles.amendmentHead}>
              <span className={styles.amendmentTitle}>
                {amendment.description}
              </span>
              <span
                className={cx(
                  styles.amendmentAmount,
                  amendment.customerAmount < 0 && styles.negative,
                )}
              >
                {formatMoney(amendment.customerAmount)}
              </span>
            </div>
            {amendment.internalNote && (
              <span className={styles.amendmentNote}>
                {amendment.internalNote}
              </span>
            )}
            <span className={styles.amendmentMeta}>
              {amendment.visibility}
              {' · Pro cut '}
              <span
                className={cx(
                  styles.mono,
                  amendment.providerAmount < 0 && styles.negative,
                )}
              >
                {formatMoney(amendment.providerAmount)}
              </span>
              {' · '}
              {shortDate(amendment.dateCreated)}
            </span>
          </li>
        ))}
      </ul>
    </JobDetailsRailGroup>
  )
}

export interface JobDetailsRailAdminGroupProps {
  children: ReactNode
}

export function JobDetailsRailAdminGroup({
  children,
}: JobDetailsRailAdminGroupProps) {
  return (
    <JobDetailsRailGroup
      title="Admin"
      action={<span className={styles.adminNote}>Admins only</span>}
    >
      {children}
    </JobDetailsRailGroup>
  )
}

function shortDate(date: Date) {
  return format(date, 'MMM d, yyyy')
}
